import type {
	ModuleFull,
	GrasslandResponse,
} from "@features/modules/grasslandManagement/types";
import {
	grasslandResponseSchema,
} from "@features/modules/grasslandManagement/types";
import { apiSlice, publicApiSlice, TagTypes } from "../../apiSlice";
import { createModuleGetApi, createModuleUpdateApi } from "./factories/createModuleApi";
import { transformModuleResponse, prepareModulePayload } from "./factories/moduleApiUtils";
import { FEModules, FEModulesObj } from "@utils/moduleList";

const includedPropList = [
	"id",
	"status",
	"is_fire_used_start",
	"is_fire_used_w",
	"is_fire_used_wo",
	"fire_periodicity_start",
	"fire_periodicity_w",
	"fire_periodicity_wo",
	"fire_impact_start",
	"fire_impact_w",
	"fire_impact_wo",
	"yield_start",
	"yield_w",
	"yield_wo",
	"area",
	"note",
	"land_use_change",
	"land_use_type_start",
	"land_use_type_w",
	"land_use_type_wo",
	"land_use_type_thread",
	"grassland_management_type_start",
	"grassland_management_type_w",
	"grassland_management_type_wo",
	"is_fire_used_thread",
	"fire_periodicity_thread",
	"fire_impact_thread",
	"yield_thread",
	"grassland_management_type_thread",
];
const includedT2PropList = [
	"combustion_factor_t2_start",
	"combustion_factor_t2_w",
	"combustion_factor_t2_wo",
	"soc_t2_start",
	"soc_t2_w",
	"soc_t2_wo",
	"fmg_t2_start",
	"fmg_t2_w",
	"fmg_t2_wo",
	"fi_t2_start",
	"fi_t2_w",
	"fi_t2_wo",
	"flu_t2_start",
	"flu_t2_w",
	"flu_t2_wo",
	"biomass_t2_start",
	"biomass_t2_w",
	"biomass_t2_wo",
	"soil_carbon_t2_start",
	"soil_carbon_t2_w",
	"soil_carbon_t2_wo",
	"agb_t2_start",
	"agb_t2_w",
	"agb_t2_wo",
];

// Define constants for the module
const MODULE_TYPE = "Grasslands";
const TAG: TagTypes = "Grassland";
const TAG_DEFAULTS: TagTypes = "GrasslandDefaults";
const ENDPOINT = FEModulesObj[FEModules.Grassland].path;

const t1PercentageProps = ["fire_impact"];
const t2PercentageProps = ["combustion_factor_t2"];

const transformResponse = (response: unknown) => {
	const parsed = grasslandResponseSchema.safeParse(response);
	if (!parsed.success) {
		throw new Error(
			`Grassland API response validation failed: ${parsed.error.message}`
		);
	}
	return transformModuleResponse<GrasslandResponse, ModuleFull>({
		response: parsed.data as GrasslandResponse,
		includedPropList,
		includedT2PropList,
		percentageProps: t1PercentageProps,
		t2PercentageProps,
	});
};

const grasslandApiSlice = createModuleGetApi<ModuleFull>({
	apiSlice,
	path: ENDPOINT,
	moduleName: MODULE_TYPE,
	tag: TAG,
	tagDefaults: TAG_DEFAULTS,
	transformResponse,
})

const publicGrasslandApiSlice = createModuleGetApi<ModuleFull>({
	apiSlice: publicApiSlice,
	path: ENDPOINT,
	moduleName: MODULE_TYPE,
	tag: TAG,
	tagDefaults: TAG_DEFAULTS,
	transformResponse,
})

const transformUpdatePayload = (data: Partial<ModuleFull>, activityId: number) => prepareModulePayload({
	moduleData: data.module,
	tiertwoData: data.tiertwo,
	percentageProps: t1PercentageProps,
	t2PercentageProps,
	activityId
})
const mutationApiSlice = createModuleUpdateApi<ModuleFull, GrasslandResponse>({
	apiSlice,
	path: ENDPOINT,
	moduleName: MODULE_TYPE,
	tag: TAG,
	tagDefaults: TAG_DEFAULTS,
	transformUpdatePayload,
	includesLandUseChange: true,
})

export const {
	useUpdateGrasslandsMutation,
} = mutationApiSlice;

export const {
	useGetGrasslandsQuery: usePublicGetGrasslandsQuery,
	useGetGrasslandsDefaultsQuery: usePublicGetGrasslandsDefaultsQuery,
} = publicGrasslandApiSlice;

export const {
	useGetGrasslandsQuery,
	useGetGrasslandsDefaultsQuery
} = grasslandApiSlice;

export const grasslandEndpoints = grasslandApiSlice.endpoints;
export const grasslandMutationEndpoints = mutationApiSlice.endpoints;